import { ChevronRight, GripVertical, Minus, Plus, Search } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { EmptyState } from '@/components/ui/EmptyState'
import { Select } from '@/components/ui/Select'
import { toNumber } from '@/lib/decimal'
import { formatContractHours, formatEuros } from '@/lib/format'
import { beginPlanningEdit } from '@/lib/planning-refresh'
import type { MonthKey, Opportunity, OpportunityStage, PlanningOverview, PlanningStage } from '@/lib/planning-types'
import { cn } from '@/lib/utils'
import { EditableHours } from './EditableHours'
import { monthLabel, monthsBetween, monthShort, monthYear, shiftMonth } from './month'
import { buildMonthAxis, type MonthEdge, MONTH_HOLD_REASON } from './month-axis'
import { MonthRangeFill } from './MonthRangeFill'
import { OpportunityShift } from './OpportunityShift'
import { countsIn } from './stages'

const STAGE_OPTIONS: { value: OpportunityStage; label: string }[] = [
  { value: 'LIKELY', label: 'Waarschijnlijk' },
  { value: 'POSSIBLE', label: 'Mogelijk' },
  { value: 'SPECULATIVE', label: 'Speculatief' },
]

const UNASSIGNED = 'unassigned'

/** Zelf gesleept, of de lijst zichzelf laten ordenen. */
export type OpportunitySort = 'manual' | 'size' | 'chance' | 'name'

export const SORT_OPTIONS: { value: OpportunitySort; label: string }[] = [
  { value: 'manual', label: 'Eigen volgorde' },
  { value: 'size', label: 'Meeste uren eerst' },
  { value: 'chance', label: 'Grootste kans eerst' },
  { value: 'name', label: 'Naam A-Z' },
]

/**
 * Het meeste werk in dit venster bovenaan - dat is het getal dat rechts in de
 * regel staat, dus de volgorde is af te lezen zonder hem uit te leggen - en wat
 * hier geen uren heeft zakt naar onderen, op volgorde van wanneer het begint.
 */
function bySize(a: Opportunity, b: Opportunity): number {
  const hours = toNumber(b.totals.hours) - toNumber(a.totals.hours)
  if (hours !== 0) return hours

  const start = (a.span.first ?? '9999-99').localeCompare(b.span.first ?? '9999-99')

  return start !== 0 ? start : a.name.localeCompare(b.name, 'nl')
}

export interface OpportunityListHandlers {
  onRestore: (opportunityId: string) => void
  onReorder: (orderedIds: string[]) => void
  onSetAllocation: (opportunityId: string, personId: string | null, month: MonthKey, hours: string) => void
  /** Een reeks maanden in een keer; landt in zijn geheel of helemaal niet. */
  onSetAllocations: (opportunityId: string, rows: { personId: string | null; month: MonthKey; hours: string }[]) => void
  onSetRevenue: (opportunityId: string, month: MonthKey, amount: string) => void
  /** De hele kans opschuiven; positief is later, negatief eerder. */
  onShift: (opportunityId: string, months: number) => void
  onSetStage: (opportunityId: string, stage: OpportunityStage) => void
  onEdit: (opportunity: Opportunity) => void
  onNew: () => void
}

/**
 * De kansen als ledger-lijst, in dezelfde grammatica als de facturenlijst. Een
 * regel klapt ter plekke uit naar zijn eigen maandrastertje, zoals de groepen op
 * het uitbetaaloverzicht: uitklappen, typen, volgende regel, zonder de ceremonie
 * van een venster dat open en dicht gaat.
 */
export function OpportunityList({
  overview,
  rows: shown,
  filtering,
  stages,
  handlers,
  open,
  onOpenChange,
  sort,
  onManualOrder,
}: {
  overview: PlanningOverview
  /** De kansen die getoond worden; het overzicht blijft de bron voor personen en maanden. */
  rows: Opportunity[]
  /** Er staat een zoekterm: de lijst is een selectie en geen volgorde om te verzetten. */
  filtering: boolean
  /** Welke zekerheid meetelt in de maanden erboven; de lijst zelf toont alles. */
  stages: PlanningStage[]
  handlers: OpportunityListHandlers
  /** Van buiten bestuurd, zodat het persoonspaneel een kans kan openwijzen. */
  open: string | null
  onOpenChange: (id: string | null) => void
  sort: OpportunitySort
  /** Slepen is zelf ordenen; de lijst schakelt daarvoor terug naar de eigen volgorde. */
  onManualOrder: () => void
}) {
  // De eigen volgorde staat al goed in de data; zolang er niets versleept is valt
  // die terug op omvang.
  const sorted = useMemo(() => {
    const rows = shown
    if ('manual' === sort && rows.some((opportunity) => opportunity.position !== null)) return rows

    if ('name' === sort) {
      return [...rows].sort((a, b) => a.name.localeCompare(b.name, 'nl'))
    }

    // Op kans staan gewonnen klussen bovenaan en zakt het speculatieve werk naar
    // onderen. De trap kent maar vier waardes, dus gelijkspel is eerder regel dan
    // uitzondering; binnen dezelfde zekerheid beslist de omvang, want tussen twee
    // even waarschijnlijke klussen telt de grootste het zwaarst.
    if ('chance' === sort) {
      return [...rows].sort((a, b) => {
        const chance = b.weightPercentage - a.weightPercentage

        return chance !== 0 ? chance : bySize(a, b)
      })
    }

    return [...rows].sort(bySize)
  }, [shown, sort])

  // Wat je zelf hebt neergelegd telt tot de server het bevestigt, zodat de rij niet
  // even terugspringt. Zodra er een kans bij komt of afvalt is die momentopname
  // niet meer geldig.
  const [dragged, setDragged] = useState<string[] | null>(null)

  const opportunities = useMemo(() => {
    if (null === dragged || dragged.length !== sorted.length) return sorted
    const byId = new Map(sorted.map((opportunity) => [opportunity.id, opportunity]))

    return dragged.every((id) => byId.has(id)) ? dragged.map((id) => byId.get(id)!) : sorted
  }, [sorted, dragged])

  // Slepen op pointer-events en niet op de sleepfunctie van de browser zelf: die
  // geeft zijn gegevens pas bij het loslaten vrij, dus tijdens het slepen weet je
  // niet wat je vasthebt. De lijst blijft ondertussen staan en laat alleen een
  // streep zien waar de regel terechtkomt - meeschuiven leest aardig in een demo,
  // maar dan sleep je achter een lijst aan die onder je cursor van vorm verandert.
  const listRef = useRef<HTMLUListElement>(null)
  const ghostRef = useRef<HTMLDivElement>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [dropAt, setDropAt] = useState<number | null>(null)
  const [ghost, setGhost] = useState<{ width: number } | null>(null)
  const dropAtRef = useRef<number | null>(null)

  const dragging = opportunities.find((opportunity) => opportunity.id === dragId)

  // De positie leeft in een ref en niet in state: de kopie moet elke muisbeweging
  // volgen zonder de lijst opnieuw te renderen. De koppeling zet hem meteen goed
  // bij het aanhaken, want bij het indrukken bestaat het element nog niet.
  const ghostPos = useRef({ x: 0, y: 0 })
  const paint = () => {
    if (ghostRef.current) {
      ghostRef.current.style.transform = `translate3d(${ghostPos.current.x}px, ${ghostPos.current.y}px, 0)`
    }
  }
  const attachGhost = (node: HTMLDivElement | null) => {
    ghostRef.current = node
    paint()
  }

  const reorder = (ids: string[], from: number, to: number) => {
    if (from < 0 || to < 0 || to >= ids.length || from === to) return
    const next = [...ids]
    next.splice(to, 0, ...next.splice(from, 1))
    setDragged(next)
    onManualOrder()
    handlers.onReorder(next)
  }

  const grab = (id: string, index: number) => (event: React.PointerEvent) => {
    // Alleen de linkerknop, en bij twee vingers telt alleen de eerste.
    if (filtering || 0 !== event.button || !event.isPrimary) return
    event.preventDefault()

    setDragId(id)
    setDropAt(index)
    dropAtRef.current = index
    document.body.classList.add('cursor-grabbing')
    // Slepen is ook bewerken: een verversing die halverwege binnenkomt herschikt
    // de lijst onder je vinger, en dan laat je de regel ergens anders los dan waar
    // je hem naartoe bracht.
    const resume = beginPlanningEdit()

    // De kopie pakt de regel op waar je hem vastpakt, zodat hij niet onder de
    // cursor vandaan springt.
    const row = (event.currentTarget as HTMLElement).closest('li')?.getBoundingClientRect()
    const offset = { x: event.clientX - (row?.left ?? 0), y: event.clientY - (row?.top ?? 0) }
    setGhost({ width: row?.width ?? 0 })

    let at = { x: event.clientX, y: event.clientY }
    const place = () => {
      ghostPos.current = { x: at.x - offset.x, y: at.y - offset.y }
      paint()
    }

    // Elke keer opnieuw opmeten in plaats van eenmalig: bij een lange lijst
    // scroll je tijdens het slepen, en dan wijzen vastgelegde randen naar de
    // plek waar de regel stond en niet naar waar hij nu staat.
    const aim = () => {
      const rows = Array.from(listRef.current?.children ?? [])
      const above = rows.findIndex((candidate) => {
        const box = candidate.getBoundingClientRect()

        return at.y < box.top + box.height / 2
      })
      const target = -1 === above ? rows.length : above

      dropAtRef.current = target
      setDropAt(target)
    }

    const move = (moved: PointerEvent) => {
      at = { x: moved.clientX, y: moved.clientY }
      // De kopie rechtstreeks verplaatsen in plaats van via state: anders rendert
      // de hele lijst bij elke beweging opnieuw.
      place()
      aim()
    }

    /*
     * Meeschuiven bij de rand. Op een telefoon is de lijst langer dan het scherm,
     * en omdat de greep het vegen overneemt kun je er zelf niet meer langs
     * scrollen. Dit loopt door zolang je bij de rand blijft hangen - dus ook als
     * je vinger stilstaat, want dan komen er geen bewegingen meer binnen.
     */
    let frame = requestAnimationFrame(function edge() {
      const margin = 72
      const above = at.y - margin
      const below = window.innerHeight - margin - at.y
      const step = above < 0 ? Math.max(above, -18) : below < 0 ? Math.min(-below, 18) : 0

      if (0 !== step) {
        window.scrollBy(0, step)
        aim()
      }
      frame = requestAnimationFrame(edge)
    })

    const stop = (commit: boolean) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', cancel)
      cancelAnimationFrame(frame)

      if (commit) {
        const ids = opportunities.map((opportunity) => opportunity.id)
        const target = dropAtRef.current
        // Het invoegpunt telt de regel zelf nog mee; die valt straks weg.
        reorder(ids, index, null === target ? -1 : target > index ? target - 1 : target)
      }

      setDragId(null)
      setDropAt(null)
      setGhost(null)
      dropAtRef.current = null
      document.body.classList.remove('cursor-grabbing')
      resume()
    }

    const release = () => stop(true)
    // Een afgebroken aanraking is geen keuze: het systeem neemt de vinger over
    // (een telefoontje, een veeg vanaf de rand). Dan hoort er niets te verschuiven.
    const cancel = () => stop(false)

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', cancel)
  }

  if (overview.opportunities.length === 0) {
    // Padding op een omhulsel en geen marge op de lege staat zelf: de kaart is
    // `inset` (dus zonder eigen padding), en dan collapst de ondermarge van het
    // laatste kind door de kaartrand heen. Onderin bleef daardoor niets over.
    return (
      <div className="p-5">
        <EmptyState
          icon={Plus}
          title="Nog geen kansen"
          description="Voer in wat je verwacht: lopend werk als gewonnen kans, offertes en prospects met een kans. Of vul een maand met een voorstel uit de historie."
          action={
            <button type="button" className="btn btn-primary" onClick={handlers.onNew}>
              Kans toevoegen
            </button>
          }
        />
      </div>
    )
  }

  return (
    <>
      {opportunities.length === 0 ? (
        <div className="p-5">
          <EmptyState
            icon={Search}
            title="Geen kansen gevonden"
            description="Pas de zoekopdracht aan of wis hem om de hele lijst weer te zien."
          />
        </div>
      ) : (
        <ul ref={listRef} className={cn('divide-y divide-hairline', null !== dragId && 'select-none')}>
          {opportunities.map((opportunity, index) => (
            <li
              key={opportunity.id}
              /*
               * Een kans die buiten de keuze valt blijft gewoon bereikbaar, maar
               * vraagt niets van je: dezelfde gedempte toon als een uitbetaalregel
               * die klaar is. Hij komt terug zodra je eroverheen gaat.
               */
              className={cn(
                'relative',
                dragId === opportunity.id
                  ? 'opacity-40'
                  : countsIn(opportunity, stages)
                    ? undefined
                    : 'opacity-60 transition-opacity hover:opacity-100',
              )}
              data-testid="kans-regel"
            >
              <DropLine visible={dropAt === index && null !== dragId} />
              <Row
                opportunity={opportunity}
                counted={countsIn(opportunity, stages)}
                expanded={open === opportunity.id}
                onToggle={() => onOpenChange(open === opportunity.id ? null : opportunity.id)}
                handlers={handlers}
                locked={filtering}
                onGrab={grab(opportunity.id, index)}
                onNudge={(direction) => {
                  const ids = opportunities.map((row) => row.id)
                  reorder(ids, index, index + direction)
                }}
              />
              {open === opportunity.id ? (
                <Editor opportunity={opportunity} overview={overview} handlers={handlers} />
              ) : null}
              {index === opportunities.length - 1 ? (
                <DropLine visible={dropAt === opportunities.length && null !== dragId} atEnd />
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <LostOpportunities lost={overview.lost} onRestore={handlers.onRestore} />

      {ghost && dragging
        ? createPortal(
            <div
              ref={attachGhost}
              aria-hidden
              data-testid="drag-ghost"
              style={{ width: ghost.width }}
              className="pointer-events-none fixed left-0 top-0 z-50 rounded-card border border-hairline bg-surface px-5 py-3 opacity-90 shadow-pop"
            >
              <span className="block truncate text-sm font-medium text-ink">{dragging.name}</span>
              <span className="block truncate text-xs text-ink-faint">{contextLine(dragging)}</span>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

/** Waar de kans bij hoort, in een regel: klant, project, kartrekker en looptijd. */
function contextLine(opportunity: Opportunity): string {
  return [
    opportunity.client ? opportunity.client.name : 'Prospect',
    opportunity.project?.name,
    opportunity.client?.internal ? 'intern' : null,
    opportunity.project?.lead?.displayName,
    spanLabel(opportunity),
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Waar de regel terechtkomt. Buiten de stroom en zonder eigen hoogte, zodat de
 * lijst er niet van opschuift terwijl je erboven hangt.
 */
function DropLine({ visible, atEnd }: { visible: boolean; atEnd?: boolean }) {
  if (!visible) return null

  return (
    <span
      aria-hidden
      data-testid="drop-line"
      className={cn(
        'pointer-events-none absolute inset-x-0 z-10 h-0.5 rounded-full bg-accent-600',
        atEnd ? '-bottom-px' : '-top-px',
      )}
    />
  )
}

/**
 * Verloren kansen tellen nergens meer mee en staan bewust niet in de lijst. Maar
 * onvindbaar mogen ze niet worden: een misklik hoort terug te draaien te zijn.
 */
function LostOpportunities({
  lost,
  onRestore,
}: {
  lost: PlanningOverview['lost']
  onRestore: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  if (lost.length === 0) return null

  return (
    <div className="border-t border-hairline px-5 py-3">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-xs text-ink-faint transition-colors hover:text-ink-soft focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} strokeWidth={1.85} />
        {lost.length} verloren
      </button>

      {open ? (
        <ul className="mt-2 space-y-1.5">
          {lost.map((item) => (
            <li key={item.id} className="flex items-center gap-3 text-sm text-ink-soft">
              <span className="min-w-0 flex-1 truncate">
                {item.name}
                {item.clientName ? <span className="text-ink-faint"> · {item.clientName}</span> : null}
              </span>
              <button type="button" className="btn btn-ghost h-7 px-2 text-xs" onClick={() => onRestore(item.id)}>
                Terugzetten
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function Row({
  opportunity,
  counted,
  expanded,
  onToggle,
  handlers,
  locked,
  onGrab,
  onNudge,
}: {
  opportunity: Opportunity
  /** Telt deze kans mee in de maanden erboven, of staat zijn trap uit? */
  counted: boolean
  expanded: boolean
  onToggle: () => void
  handlers: OpportunityListHandlers
  /** De volgorde is niet te verzetten vanuit een selectie. */
  locked: boolean
  /** Slepen begint bij de greep, niet ergens op de regel. */
  onGrab: (event: React.PointerEvent) => void
  onNudge: (direction: 1 | -1) => void
}) {
  const won = opportunity.status === 'WON'
  const hours = toNumber(opportunity.totals.hours)
  const revenue = toNumber(opportunity.totals.revenue)
  const outside = toNumber(opportunity.outsideWindow.hours)

  return (
    <div className="group flex items-center gap-3.5 px-5 py-3 hover:bg-fill-1">
      {/*
        Op de muis verschijnt de greep pas als je in de buurt bent - hij hoort bij
        het verzetten van werk, niet bij het lezen ervan - maar op touch bestaat
        "in de buurt" niet, dus daar staat hij altijd. Dezelfde rij-actieregel als
        elders. `touch-none` geeft de veeg aan ons in plaats van aan de browser;
        zonder dat scrollt de pagina weg en breekt het slepen halverwege af.

        Vastgezet met `aria-disabled` en niet met `disabled`: een uitgeschakelde
        knop krijgt geen muisgebeurtenissen meer, en dan is de uitleg waarom je nu
        niet kunt slepen onbereikbaar - juist die is hier het hele punt.
      */}
      <button
        type="button"
        aria-label={`Verplaats ${opportunity.name}`}
        aria-disabled={locked || undefined}
        title={locked ? 'Slepen kan niet terwijl je zoekt: de volgorde geldt voor de hele lijst, niet voor een selectie' : undefined}
        className={cn(
          '-ml-3 shrink-0 touch-none text-ink-faint transition-opacity focus-visible:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40 md:opacity-0 md:group-hover:opacity-100',
          locked ? 'cursor-not-allowed opacity-40 md:group-hover:opacity-40' : 'cursor-grab active:cursor-grabbing',
        )}
        onPointerDown={onGrab}
        onKeyDown={(event) => {
          if (locked || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return
          event.preventDefault()
          onNudge(event.key === 'ArrowUp' ? -1 : 1)
        }}
      >
        <GripVertical className="h-4 w-4" strokeWidth={1.85} />
      </button>

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
      >
        <ChevronRight
          className={cn('h-4 w-4 shrink-0 text-ink-faint transition-transform', expanded && 'rotate-90')}
          strokeWidth={1.85}
        />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-ink">{opportunity.name}</span>
          {/* Gedempt alleen is te weinig: wie het verschil in dekking niet ziet
              hoort te kunnen lezen waarom deze regel niet in de cijfers zit. */}
          <span className="block truncate text-xs text-ink-faint">
            {contextLine(opportunity)}
            {counted ? null : ' · telt niet mee'}
          </span>
        </span>
      </button>

      <Notices opportunity={opportunity} />

      {won ? (
        <span className="hidden shrink-0 items-center gap-1.5 rounded-md bg-fill-1 px-2 py-0.5 text-xs text-ink-soft ring-1 ring-hairline sm:inline-flex">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-accent-600" />
          Gewonnen
        </span>
      ) : (
        <Select
          value={opportunity.stage}
          onValueChange={(value) => handlers.onSetStage(opportunity.id, value as OpportunityStage)}
          options={STAGE_OPTIONS}
          className="hidden h-7 w-[150px] shrink-0 text-xs sm:flex"
        />
      )}

      <span className="num hidden w-16 shrink-0 text-right text-xs text-ink-soft sm:block">
        {hours > 0 ? `${formatContractHours(hours)} u` : '-'}
      </span>
      {/* Een kans die helemaal buiten het venster valt zou anders als 0,00 lezen,
          terwijl er wel degelijk werk en omzet in zit. */}
      <span className={cn('money w-24 shrink-0 text-right text-sm', revenue > 0 ? 'text-ink' : 'text-ink-faint')}>
        {revenue > 0 ? formatEuros(revenue) : outside > 0 ? 'later' : '-'}
      </span>

      <button
        type="button"
        onClick={() => handlers.onEdit(opportunity)}
        className="btn btn-ghost h-7 shrink-0 px-2 text-xs text-ink-faint"
      >
        Bewerken
      </button>
    </div>
  )
}

/**
 * De omvang van de klus tegenover wat er al in het rastertje staat. Alleen als er
 * een omvang is opgegeven: zonder dat getal valt er niets te verdelen en is het
 * rastertje zelf het hele verhaal.
 */
function Distribution({ opportunity }: { opportunity: Opportunity }) {
  const { total, distributed, undistributed } = opportunity.distribution
  if (total === null || undistributed === null) return null

  const rest = toNumber(undistributed)
  const hours = (value: string) => `${formatContractHours(toNumber(value))} u`

  return (
    <p className="text-xs text-ink-faint">
      Omvang {hours(total)} · verdeeld {hours(distributed)} ·{' '}
      <span className={cn(rest !== 0 && 'text-ink-soft')}>
        {rest === 0
          ? 'helemaal verdeeld'
          : rest > 0
            ? `${formatContractHours(rest)} u onverdeeld`
            : `${formatContractHours(-rest)} u te veel verdeeld`}
      </span>
    </p>
  )
}

/**
 * De aandachtspunten staan op de regel waar je er iets mee kunt, als korte zin
 * met een stip; dezelfde vorm als de wachtregels bij uitbetalingen.
 */
function Notices({ opportunity }: { opportunity: Opportunity }) {
  if (opportunity.notices.length === 0) return null

  return (
    <span className="hidden shrink-0 items-center gap-3 md:flex">
      {opportunity.notices.map((notice) => (
        <span key={notice.key} className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-status-overdue" />
          {notice.text}
        </span>
      ))}
    </span>
  )
}

function spanLabel(opportunity: Opportunity): string | null {
  const { first, last } = opportunity.span
  if (!first) return null
  const year = monthYear(first)
  if (!last || first === last) return monthShort(first, year)

  return `${monthShort(first, year)} t/m ${monthShort(last, year)}`
}

/**
 * De maandas een maand langer maken. Blijft binnen dit rastertje: het venster
 * eronder verzetten om een enkele maand te kunnen vullen is een grote beweging
 * voor een kleine vraag.
 */
function ExtendMonths({ label, onAdd }: { label: string; onAdd: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onAdd}
      className="btn btn-ghost h-6 w-6 p-0 text-ink-faint"
    >
      <Plus className="h-3.5 w-3.5" strokeWidth={1.85} />
    </button>
  )
}

/**
 * De tegenhanger: de as weer een maand korter. Hij staat aan de binnenkant van de
 * `+`, tegen de maand aan die hij weghaalt, zodat elke knop staat waar zijn
 * gevolg zit.
 *
 * Inkorten is kijken en geen wissen, dus een maand met uren of een bedrag blijft
 * staan. Vastgezet met `aria-disabled` en niet met `disabled`: een uitgeschakelde
 * knop krijgt geen muisgebeurtenissen, en dan is de uitleg waarom deze maand
 * blijft onbereikbaar.
 */
function TrimMonths({ label, edge, onTrim }: { label: string; edge: MonthEdge | null; onTrim: () => void }) {
  const held = edge?.held ?? null

  return (
    <button
      type="button"
      aria-label={label}
      title={held ? MONTH_HOLD_REASON[held] : label}
      aria-disabled={held ? true : undefined}
      onClick={() => {
        if (!held) onTrim()
      }}
      className={cn('btn btn-ghost h-6 w-6 p-0 text-ink-faint', held && 'cursor-not-allowed opacity-40')}
    >
      <Minus className="h-3.5 w-3.5" strokeWidth={1.85} />
    </button>
  )
}

/**
 * Het maandrastertje van een enkele kans. Naast de venstermaanden komen ook de
 * maanden mee waar al uren staan: anders moet je eerst bladeren voor je werk naar
 * voren of naar achteren kunt schuiven.
 */
function Editor({
  opportunity,
  overview,
  handlers,
}: {
  opportunity: Opportunity
  overview: PlanningOverview
  handlers: OpportunityListHandlers
}) {
  const [extraRows, setExtraRows] = useState<string[]>([])
  // Maanden die je zelf aan het raster hebt geplakt. Ze horen bij dit rastertje en
  // niet bij het venster: een maand ervoor of erna aanzetten mag het scherm
  // eronder niet verschuiven.
  const [extraMonths, setExtraMonths] = useState<MonthKey[]>([])

  /**
   * De as loopt van de eerste tot en met de laatste maand die ergens vandaan komt:
   * het venster, wat je zelf hebt aangezet, en elke maand met uren of een bedrag.
   * De maanden waar iets staat bepalen hem niet alleen, want dan verdwijnt een
   * maand op het moment dat je hem leegmaakt en kun je er niets meer intypen;
   * daarom houdt elke maand die je in deze sessie hebt aangeraakt zijn kolom.
   */
  const axis = buildMonthAxis({
    windowMonths: overview.months,
    extra: extraMonths,
    filled: [
      ...opportunity.allocations.map((allocation) => allocation.month),
      ...opportunity.revenues.map((revenue) => revenue.month),
    ],
  })
  const months = axis.months

  // Inkorten laat alleen los wat je zelf hebt aangezet; de andere bronnen houden
  // hun kolom, dus er verdwijnt nooit iets waar werk in staat.
  const trim = (edge: MonthEdge | null) => {
    if (null === edge || null !== edge.held) return
    setExtraMonths((current) => current.filter((month) => month !== edge.month))
  }

  const rows = new Map<string, Map<MonthKey, string>>()
  for (const allocation of opportunity.allocations) {
    const key = allocation.personId ?? UNASSIGNED
    const row = rows.get(key) ?? new Map<MonthKey, string>()
    row.set(allocation.month, allocation.hours)
    rows.set(key, row)
  }
  for (const key of extraRows) if (!rows.has(key)) rows.set(key, new Map())
  // Werk waarvan je de omvang kent maar de naam nog niet is de normale
  // tussenstand van een nieuwe kans. Die regel staat er dus meteen, in plaats van
  // achter "Wie erbij", zodat je de uren kwijt kunt voor je iemand vastlegt.
  if (rows.size === 0) rows.set(UNASSIGNED, new Map())

  const nameFor = (key: string) =>
    key === UNASSIGNED ? 'Nog niet toegewezen' : (overview.persons.find((p) => p.personId === key)?.displayName ?? 'Onbekend')

  const available = [
    ...overview.persons.filter((p) => !rows.has(p.personId)).map((p) => ({ value: p.personId, label: p.displayName })),
    ...(rows.has(UNASSIGNED) ? [] : [{ value: UNASSIGNED, label: 'Nog niet toegewezen' }]),
  ]

  const reference = monthYear(months[0] ?? overview.months[0])

  // De keuzelijst van het reeks-paneel loopt bewust ruim voorbij het raster: waar
  // je werk neerlegt hoort niet af te hangen van hoe ver het scherm toevallig
  // kijkt. Drie maanden terug is genoeg om werk naar voren te halen; vooruit telt
  // hij door tot voorbij de laatste maand die al in beeld is.
  const first = shiftMonth(months[0], -3)
  const selectable = Array.from({ length: Math.max(36, monthsBetween(first, months[months.length - 1]) + 13) }, (_, step) =>
    shiftMonth(first, step),
  )

  const fill = (key: string, range: MonthKey[], hours: string) =>
    handlers.onSetAllocations(
      opportunity.id,
      range.map((month) => ({ personId: key === UNASSIGNED ? null : key, month, hours })),
    )

  return (
    <div className="border-t border-hairline bg-fill-1/40 px-5 py-4">
      <div className="overflow-x-auto">
        {/* Vaste breedte in plaats van w-full: anders trekken de maandkolommen uit
            elkaar op een breed scherm en staat het getal ver van zijn kop af. */}
        {/* De naamkolom draagt sinds de reeks-knop een extra element; zonder die
            ruimte zou "Nog niet toegewezen" erdoor afgekapt worden. */}
        <table className="table-fixed border-collapse" style={{ width: 204 + 112 + months.length * 64 }}>
          <thead>
            <tr>
              <th className="label-caps w-[204px] py-1 pr-3 text-left">Wie</th>
              {/* De maandas is uit te schuiven zonder het venster te verzetten -
                  werk begint zelden precies waar je toevallig naar kijkt - en aan
                  dezelfde kant weer in te korten. */}
              <th className="w-14 p-0">
                <span className="flex items-center">
                  <ExtendMonths label="Maand ervoor toevoegen" onAdd={() => setExtraMonths((c) => [...c, shiftMonth(months[0], -1)])} />
                  <TrimMonths label="Eerste maand weghalen" edge={axis.start} onTrim={() => trim(axis.start)} />
                </span>
              </th>
              {months.map((month) => (
                <th key={month} className="label-caps w-16 whitespace-nowrap px-1 py-1 text-right">
                  {monthShort(month, reference)}
                </th>
              ))}
              <th className="w-14 p-0">
                <span className="flex items-center">
                  <TrimMonths label="Laatste maand weghalen" edge={axis.end} onTrim={() => trim(axis.end)} />
                  <ExtendMonths
                    label="Maand erna toevoegen"
                    onAdd={() => setExtraMonths((c) => [...c, shiftMonth(months[months.length - 1], 1)])}
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {[...rows.keys()].map((key) => (
              <tr key={key} className="group/rij">
                <th scope="row" className="py-1 pr-3 text-left text-sm font-normal text-ink-soft">
                  <span className="flex items-center gap-1">
                    <span className="min-w-0 truncate">{nameFor(key)}</span>
                    <MonthRangeFill
                      who={nameFor(key)}
                      months={selectable}
                      start={months[0]}
                      onFill={(range, hours) => fill(key, range, hours)}
                    />
                  </span>
                </th>
                <td className="p-0" />
                {months.map((month) => (
                  <td key={month} className="px-1 py-0.5">
                    <EditableHours
                      value={rows.get(key)?.get(month) ?? null}
                      onCommit={(hours) => {
                        // Zowel de maandas als de rijen zijn afgeleid van waar uren
                        // staan, dus wie een cel leegmaakt ziet zijn kolom of zijn hele
                        // regel verdwijnen en kan er niets meer intypen. Allebei
                        // vastpinnen zolang de kans openstaat; sluiten en opnieuw
                        // openen laat weer zien wat er echt staat.
                        setExtraMonths((current) => (current.includes(month) ? current : [...current, month]))
                        setExtraRows((current) => (current.includes(key) ? current : [...current, key]))
                        handlers.onSetAllocation(opportunity.id, key === UNASSIGNED ? null : key, month, hours === '' ? '0' : hours)
                      }}
                      label={`Uren van ${nameFor(key)} in ${monthLabel(month)} voor ${opportunity.name}`}
                      className="text-sm"
                    />
                  </td>
                ))}
                <td className="p-0" />
              </tr>
            ))}

            {opportunity.revenueModel === 'FIXED' ? (
              <tr>
                <th scope="row" className="py-1 pr-3 text-left text-sm font-normal text-ink-soft">
                  Bedrag
                </th>
                <td className="p-0" />
                {months.map((month) => (
                  <td key={month} className="px-1 py-0.5">
                    <EditableHours
                      money
                      value={opportunity.revenues.find((r) => r.month === month)?.amount ?? null}
                      onCommit={(amount) => handlers.onSetRevenue(opportunity.id, month, amount === '' ? '0' : amount)}
                      label={`Bedrag in ${monthLabel(month)} voor ${opportunity.name}`}
                      className="text-xs"
                    />
                  </td>
                ))}
                <td className="p-0" />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        {available.length > 0 ? (
          <Select
            value=""
            onValueChange={(value) => setExtraRows((current) => [...current, value])}
            options={available}
            placeholder="Wie erbij"
            className="h-8 w-[200px] text-xs"
          />
        ) : null}
        <OpportunityShift
          opportunity={opportunity}
          onShift={(months) => {
            // De vastgepinde maanden hoorden bij waar het werk stond; nu het
            // verhuisd is zouden ze als lege kolommen achterblijven.
            setExtraMonths([])
            handlers.onShift(opportunity.id, months)
          }}
        />
        <Distribution opportunity={opportunity} />
      </div>
    </div>
  )
}
